import React, {useEffect, useId} from 'react';
import {useForm} from 'react-hook-form';
import {Box, Button, Chip, DialogActions, DialogContent, DialogTitle, Typography} from '@mui/material';
import {FolderSharedOutlined} from '@mui/icons-material';
import {alpha} from '@mui/material/styles';
import PropTypes from 'prop-types';
import WorkspaceDropdown from './WorkspaceDropdown';
import {SettingsDialog, SettingsDialogDescription} from '../settings/SettingsDialog';
import {getSettingsColors} from '../settings/SettingsDesign';

function ChangeWorkspacePopUp({onClose, open, onSubmit, selectedCount}) {
    const formId = useId();
    const {handleSubmit, control, setValue, reset} = useForm({defaultValues: {workspace: null}});

    useEffect(() => {
        if (!open) reset();
    }, [open, reset]);

    const handleClose = (event, reason) => {
        if (reason !== 'backdropClick') onClose();
    };

    return <SettingsDialog open={open} onClose={handleClose} fullWidth maxWidth="sm"
        aria-labelledby="change-workspace-title" aria-describedby="change-workspace-description"
        sx={{'& .MuiDialog-paper': {borderRadius: '16px'},
            '& .MuiDialog-paper .MuiAutocomplete-root .MuiOutlinedInput-root': {
                bgcolor: theme => theme.palette.mode === 'light' ? theme.palette.background.paper : getSettingsColors(theme).input
            },
            '& .MuiDialogTitle-root + .MuiDialogContent-root': {pt: 1.5},
            '& .MuiButton-root': {textTransform: 'none', minHeight: 36}}}>
        <DialogTitle id="change-workspace-title">
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
                <Box sx={{display: 'flex', p: 1, borderRadius: '10px', color: 'primary.main',
                    bgcolor: theme => alpha(theme.palette.primary.main, 0.1)}}><FolderSharedOutlined/></Box>
                <Box component="span">Bulk update for workspace</Box>
            </Box>
            <SettingsDialogDescription>
                <Box component="span" id="change-workspace-description">Move all selected records to a different workspace.</Box>
            </SettingsDialogDescription>
        </DialogTitle>
        <DialogContent>
            <Box component="form" id={formId} onSubmit={handleSubmit(data => onSubmit({workspaceId: data.workspace.value}))}>
                <Box sx={{p: 2.5, borderRadius: '12px', bgcolor: theme => getSettingsColors(theme).inset,
                    '& .MuiTextField-root': {my: 0}}}>
                    <Box sx={{display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2}}>
                        <Typography component="h3" sx={{fontSize: 14, fontWeight: 600}}>Destination workspace</Typography>
                        {selectedCount > 0 && <Chip size="small" label={`${selectedCount} ${selectedCount === 1 ? 'record' : 'records'} selected`}
                            sx={{height: 24, fontSize: 11, color: 'text.secondary', bgcolor: theme => getSettingsColors(theme).card}}/>}
                    </Box>
                    <WorkspaceDropdown control={control} setValue={setValue}
                        validate={value => value?.value > 0 || 'Please select a workspace.'}/>
                    <Typography sx={{fontSize: 12, color: 'text.secondary', mt: 1.5, lineHeight: 1.65, whiteSpace: 'normal'}}>
                        Access to these records will follow the destination workspace’s permissions.
                    </Typography>
                </Box>
            </Box>
        </DialogContent>
        <DialogActions>
            <Button color="inherit" onClick={handleClose}>Cancel</Button>
            <Button type="submit" form={formId} variant="contained">Update workspace</Button>
        </DialogActions>
    </SettingsDialog>;
}

ChangeWorkspacePopUp.propTypes = {
    onClose: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired,
    onSubmit: PropTypes.func.isRequired,
    selectedCount: PropTypes.number
};

export default ChangeWorkspacePopUp;
